import { useState, useMemo, useEffect, useRef } from "react";
import { Text, IconButton, Input, InputDate, InputMoney, InputDropdown, Autocomplete, Button } from "@artprov/design-system";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { getFechaHoyArgentina, parseARtoISO } from "../../../common/utils/functions/dateUtils";
import { FORMATO_FECHA } from "../../../common/utils/constants";
import { formatCurrency } from "../../../common/utils/functions/formatCurrency";
import { getJurisdicciones, getFueros, getJuzgados, getSecretarias } from "../../../services/matchingBancario.service";

const EmailBancarioEditForm = ({
    title,
    fields,
    emailBancario,
    userName,
    onSave,
    onCancel
}) => {
    // Estados del formulario inicializados directamente con los valores del emailBancario
    const [values, setValues] = useState({
        fechaIngreso: emailBancario?.fechaIngreso || "",
        importe: formatCurrency(emailBancario?.importe) || "",
        cuenta: emailBancario?.cuenta || "",
        expediente: emailBancario?.expediente || "",
        cuij: emailBancario?.cuij || "",
        actor: emailBancario?.actor || "",
        demandado: emailBancario?.demandado || "",
        oficio: emailBancario?.oficio || "",
        tipoMedida: emailBancario?.tipoMedida || "",
        nroTramite: emailBancario?.nroTramite || "",
    });

    // Estado de errores
    const [errors, setErrors] = useState({});

    // Estado para Jurisdicción (Autocomplete)
    const [idJurisdiccion, setIdJurisdiccion] = useState(emailBancario?.idJurisdiccion || null);
    const [jurisdiccionSearch, setJurisdiccionSearch] = useState(emailBancario?.jurisdiccion || "");

    // Estado para Fuero (Autocomplete)
    const [idFuero, setIdFuero] = useState(emailBancario?.idFuero || null);
    const [fueroSearch, setFueroSearch] = useState(emailBancario?.fuero || "");

    // Estado para Juzgado (Autocomplete)
    const [idJuzgado, setIdJuzgado] = useState(emailBancario?.idJuzgado || null);
    const [juzgadoSearch, setJuzgadoSearch] = useState(emailBancario?.juzgado || "");

    // Estado para Secretaría (Autocomplete)
    const [idSecretaria, setIdSecretaria] = useState(emailBancario?.idSecretaria || null);
    const [secretariaSearch, setSecretariaSearch] = useState(emailBancario?.secretaria || "");

    // Workaround: proteger el estado durante el montaje para evitar que el Autocomplete lo anule
    const isMounting = useRef(true);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            isMounting.current = false;
        }, 300);
        return () => window.clearTimeout(timer);
    }, []);

    // Ref para detectar cambios reales de jurisdicción, fuero y juzgado (no el montaje inicial)
    const prevJurisdiccionRef = useRef(emailBancario?.idJurisdiccion);
    const prevFueroRef = useRef(emailBancario?.idFuero);
    const prevJuzgadoRef = useRef(emailBancario?.idJuzgado);

    // Query para obtener jurisdicciones
    const { data: jurisdiccionesData, isLoading: jurisdiccionesLoading } = useQuery({
        queryKey: ["jurisdicciones"],
        queryFn: getJurisdicciones,
    });

    const opcionesJurisdiccion = useMemo(() => {
        return (jurisdiccionesData || []).map((j) => ({
            id: String(j.id),
            descripcion: j.descripcion,
        }));
    }, [jurisdiccionesData]);

    // Sincronizar descripción del Autocomplete cuando cargan las opciones y hay un idJurisdiccion inicial
    useEffect(() => {
        if (opcionesJurisdiccion.length > 0 && idJurisdiccion) {
            const matched = opcionesJurisdiccion.find((o) => Number(o.id) === idJurisdiccion);
            if (matched && matched.descripcion !== jurisdiccionSearch) {
                setJurisdiccionSearch(matched.descripcion);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opcionesJurisdiccion, idJurisdiccion]);

    // Query para obtener fueros (depende de la jurisdicción seleccionada)
    const { data: fuerosData, isLoading: fuerosLoading } = useQuery({
        queryKey: ["fueros", idJurisdiccion],
        queryFn: () => getFueros(idJurisdiccion),
        enabled: !!idJurisdiccion,
    });

    const opcionesFuero = useMemo(() => {
        return (fuerosData || []).map((f) => ({
            id: String(f.id),
            descripcion: f.descripcion,
        }));
    }, [fuerosData]);

    // Sincronizar descripción del Autocomplete de fuero cuando cargan las opciones y hay un idFuero inicial
    useEffect(() => {
        if (opcionesFuero.length > 0 && idFuero) {
            const matched = opcionesFuero.find((o) => Number(o.id) === idFuero);
            if (matched) {
                if (matched.descripcion !== fueroSearch) {
                    setFueroSearch(matched.descripcion);
                }
            } else {
                // idFuero no pertenece a esta jurisdicción → limpiar
                setIdFuero(null);
                setFueroSearch("");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opcionesFuero, idFuero]);

    // Query para obtener juzgados (depende de la jurisdicción y fuero seleccionados)
    const { data: juzgadosData, isLoading: juzgadosLoading } = useQuery({
        queryKey: ["juzgados", idJurisdiccion, idFuero],
        queryFn: () => getJuzgados(idJurisdiccion, idFuero),
        enabled: !!idJurisdiccion && !!idFuero,
    });

    const opcionesJuzgado = useMemo(() => {
        return (juzgadosData || []).map((j) => ({
            id: String(j.id),
            descripcion: j.descripcion,
        }));
    }, [juzgadosData]);

    // Sincronizar descripción del Autocomplete de juzgado cuando cargan las opciones y hay un idJuzgado inicial
    useEffect(() => {
        if (opcionesJuzgado.length > 0 && idJuzgado) {
            const matched = opcionesJuzgado.find((o) => Number(o.id) === idJuzgado);
            if (matched) {
                if (matched.descripcion !== juzgadoSearch) {
                    setJuzgadoSearch(matched.descripcion);
                }
            } else {
                // idJuzgado no pertenece a esta jurisdicción+fuero → limpiar
                setIdJuzgado(null);
                setJuzgadoSearch("");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opcionesJuzgado, idJuzgado]);

    // Query para obtener secretarías (depende del juzgado seleccionado)
    const { data: secretariasData, isLoading: secretariasLoading } = useQuery({
        queryKey: ["secretarias", idJuzgado],
        queryFn: () => getSecretarias(idJuzgado),
        enabled: !!idJuzgado,
    });

    const opcionesSecretaria = useMemo(() => {
        return (secretariasData || []).map((s) => ({
            id: String(s.id),
            descripcion: s.descripcion,
        }));
    }, [secretariasData]);

    // Sincronizar descripción del Autocomplete de secretaría cuando cargan las opciones y hay un idSecretaria inicial
    useEffect(() => {
        if (opcionesSecretaria.length > 0 && idSecretaria) {
            const matched = opcionesSecretaria.find((o) => Number(o.id) === idSecretaria);
            if (matched) {
                if (matched.descripcion !== secretariaSearch) {
                    setSecretariaSearch(matched.descripcion);
                }
            } else {
                // idSecretaria no pertenece a este juzgado → limpiar
                setIdSecretaria(null);
                setSecretariaSearch("");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opcionesSecretaria, idSecretaria]);

    // Opciones hardcodeadas para Tipo de Medida
    const tiposMedidaOptions = useMemo(() => [
        { value: "EJECUTIVO", label: "EJECUTIVO" },
        { value: "PREVENTIVO", label: "PREVENTIVO" },
        { value: "NINGUNO", label: "NINGUNO" }
    ], []);

    // Validar el formulario
    const validateForm = (formValues) => {
        const newErrors = {};

        // Fecha
        if (!formValues.fechaIngreso) {
            newErrors.fechaIngreso = "Ingresá la fecha del email";
        } else {
            const hoy = getFechaHoyArgentina(FORMATO_FECHA.ISO);
            if (parseARtoISO(formValues.fechaIngreso) > hoy) {
                newErrors.fechaIngreso = "La fecha no puede ser futura";
            }
        }

        // Importe
        if (!formValues.importe || Number.parseFloat(formValues.importe) <= 0) {
            newErrors.importe = "El importe debe ser mayor a 0";
        }

        // Expediente o CUIJ (al menos uno)
        if (!formValues.expediente && !formValues.cuij) {
            newErrors.expediente = "Ingresá el expediente o el CUIJ";
            newErrors.cuij = "Ingresá el expediente o el CUIJ";
        }

        // Formato CUIJ (solo si tiene valor)
        if (formValues.cuij) {
            const cuijRegex = /^\d{2}-\d{8}-\d{1}$/;
            if (!cuijRegex.test(formValues.cuij)) {
                newErrors.cuij = "Ingresá un CUIJ válido";
            }
        }

        // Actor
        if (!formValues.actor || formValues.actor.trim() === "") {
            newErrors.actor = "Ingresá el nombre del demandante";
        }

        // Demandado
        if (!formValues.demandado || formValues.demandado.trim() === "") {
            newErrors.demandado = "Ingresá el nombre del demandado";
        }

        // Tipo de Medida
        if (!formValues.tipoMedida) {
            newErrors.tipoMedida = "Ingresá el tipo de medida";
        }

        // Jurisdicción
        if (!idJurisdiccion) {
            newErrors.jurisdiccion = "Seleccioná una jurisdicción";
        }

        // Fuero
        if (!idFuero) {
            newErrors.fuero = "Seleccioná un fuero";
        }

        // Juzgado
        if (!idJuzgado) {
            newErrors.juzgado = "Seleccioná un juzgado";
        }

        // Secretaría
        if (!idSecretaria) {
            newErrors.secretaria = "Seleccioná una secretaría";
        }

        return newErrors;
    };

    // Validación reactiva de Jurisdicción
    useEffect(() => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (!idJurisdiccion) {
                if (!jurisdiccionSearch || jurisdiccionSearch.trim() === "") {
                    newErrors.jurisdiccion = "Seleccioná una jurisdicción";
                } else {
                    newErrors.jurisdiccion = "Seleccioná una jurisdicción válida";
                }
            } else {
                delete newErrors.jurisdiccion;
            }
            return newErrors;
        });
    }, [jurisdiccionSearch, idJurisdiccion]);

    // Validación reactiva de Fuero
    useEffect(() => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (!idFuero) {
                if (!fueroSearch || fueroSearch.trim() === "") {
                    newErrors.fuero = "Seleccioná un fuero";
                } else {
                    newErrors.fuero = "Seleccioná un fuero válido";
                }
            } else {
                delete newErrors.fuero;
            }
            return newErrors;
        });
    }, [fueroSearch, idFuero]);

    // Validación reactiva de Juzgado
    useEffect(() => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (!idJuzgado) {
                if (!juzgadoSearch || juzgadoSearch.trim() === "") {
                    newErrors.juzgado = "Seleccioná un juzgado";
                } else {
                    newErrors.juzgado = "Seleccioná un juzgado válido";
                }
            } else {
                delete newErrors.juzgado;
            }
            return newErrors;
        });
    }, [juzgadoSearch, idJuzgado]);

    // Validación reactiva de Secretaría
    useEffect(() => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (!idSecretaria) {
                if (!secretariaSearch || secretariaSearch.trim() === "") {
                    newErrors.secretaria = "Seleccioná una secretaría";
                } else {
                    newErrors.secretaria = "Seleccioná una secretaría válida";
                }
            } else {
                delete newErrors.secretaria;
            }
            return newErrors;
        });
    }, [secretariaSearch, idSecretaria]);

    // Cascada: cuando cambia la jurisdicción, limpiar el fuero y juzgado
    useEffect(() => {
        if (prevJurisdiccionRef.current !== idJurisdiccion) {
            setIdFuero(null);
            setFueroSearch("");
            setIdJuzgado(null);
            setJuzgadoSearch("");
            prevJurisdiccionRef.current = idJurisdiccion;
        }
    }, [idJurisdiccion]);

    // Cascada: cuando cambia el fuero, limpiar el juzgado
    useEffect(() => {
        if (prevFueroRef.current !== idFuero) {
            setIdJuzgado(null);
            setJuzgadoSearch("");
            prevFueroRef.current = idFuero;
        }
    }, [idFuero]);

    // Cascada: cuando cambia el juzgado, limpiar la secretaría
    useEffect(() => {
        if (prevJuzgadoRef.current !== idJuzgado) {
            setIdSecretaria(null);
            setSecretariaSearch("");
            prevJuzgadoRef.current = idJuzgado;
        }
    }, [idJuzgado]);

    // Verificar si el formulario es válido
    const isValid =
        values.fechaIngreso &&
        values.importe &&
        Number.parseFloat(values.importe) > 0 &&
        (values.expediente || values.cuij) &&
        values.actor &&
        values.actor.trim() !== "" &&
        values.demandado &&
        values.demandado.trim() !== "" &&
        values.tipoMedida &&
        idJurisdiccion &&
        idFuero &&
        idJuzgado &&
        idSecretaria &&
        Object.keys(errors).length === 0;

    // Setter que actualiza valor y revalida
    const setValue = (name, value) => {
        const newValues = { ...values, [name]: value };
        setValues(newValues);
        // Revalidar el formulario
        const newErrors = validateForm(newValues);
        setErrors(newErrors);
    };

    // Handler de guardado
    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validateForm(values);
        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const payload = {
                fechaEmail: parseARtoISO(values.fechaIngreso),
                importeEmbargo: String(values.importe).replace(/\./g, "").replace(",", "."),
                expediente: values.expediente || null,
                cuij: values.cuij || null,
                demandante: values.actor,
                demandado: values.demandado,
                oficio: values.oficio || null,
                tipoMedida: values.tipoMedida,
                nroTramite: values.nroTramite || null,
                jurisdiccion: idJurisdiccion,
                fuero: idFuero,
                juzgado: idJuzgado,
                secretaria: idSecretaria,
                codigoEstado: emailBancario.estadoCodigo,
                user: userName,
            };
            onSave(payload);
        }
    };

    // Handler de cancelación
    const handleCancel = () => {
        if (onCancel) onCancel();
    };

    // Renderizar campo editable
    const renderEditableField = (field) => {
        const fieldName = field.editKey;
        const errorMessage = errors[fieldName] || "";
        const fieldValue = values[fieldName] ?? "";

        // Fecha del email
        if (fieldName === "fechaIngreso") {
            return (
                <InputDate
                    key={field.label}
                    label={field.label}
                    value={fieldValue}
                    onChange={(newValue) => setValue("fechaIngreso", newValue || "")}
                    hints={errorMessage ? [{ variant: 'error', text: errorMessage }] : []}
                    status={errorMessage ? 'error' : 'default'}
                />
            );
        }

        // Importe embargo
        if (fieldName === "importe") {
            return (
                <InputMoney
                    key={field.label}
                    label={field.label}
                    value={fieldValue}
                    onChange={(newValue) => setValue("importe", newValue || "")}
                    hints={errorMessage ? [{ variant: 'error', text: errorMessage }] : []}
                    status={errorMessage ? 'error' : 'default'}
                />
            );
        }

        // Cuenta bancaria (deshabilitada)
        if (fieldName === "cuenta") {
            return (
                <Input
                    key={field.label}
                    label={field.label}
                    value={fieldValue}
                    placeholder=""
                    disabled={true}
                    onChange={() => { }}
                    hints={[]}
                />
            );
        }

        // CUIJ
        if (fieldName === "cuij") {
            return (
                <Input
                    key={field.label}
                    label={field.label}
                    placeholder="Ej.: 20-12345678-9"
                    value={fieldValue}
                    showMask={false}
                    separate={false}
                    mask="xx-xxxxxxxx-x"
                    replacement={{
                        'x': /\d/,
                    }}
                    onChange={(e) => {
                        let newValue = "";
                        if (typeof e === 'string') {
                            newValue = e;
                        } else if (e?.target?.value !== undefined) {
                            newValue = e.target.value;
                        } else if (e?.value !== undefined) {
                            newValue = e.value;
                        }
                        setValue(fieldName, newValue);
                    }}
                    hints={errorMessage ? [{ variant: 'error', text: errorMessage }] : []}
                    status={errorMessage ? 'error' : 'default'}
                />
            );
        }

        // Tipo de Medida
        if (fieldName === "tipoMedida") {
            const tiposMedidaOptionsElements = tiposMedidaOptions.map((tm) => (
                <InputDropdown.Option
                    key={`tipo-medida-${tm.value}`}
                    value={tm.value}
                    selected={fieldValue === tm.value}
                >
                    {tm.label}
                </InputDropdown.Option>
            ));

            return (
                <InputDropdown
                    key={field.label}
                    label={field.label}
                    value={fieldValue}
                    placeholder="Seleccionar tipo de medida"
                    onChange={({ value }) => setValue("tipoMedida", value || "")}
                    hints={errorMessage ? [{ variant: 'error', text: errorMessage }] : []}
                    status={errorMessage ? 'error' : 'default'}
                >
                    {tiposMedidaOptionsElements}
                </InputDropdown>
            );
        }

        // Jurisdicción
        if (fieldName === "jurisdiccion") {
            const jurisdiccionError = errors[fieldName] || "";
            return (
                <Autocomplete
                    key={field.label}
                    label={field.label}
                    placeholder="Seleccionar jurisdicción"
                    opciones={opcionesJurisdiccion}
                    cargando={jurisdiccionesLoading}
                    searchValue={jurisdiccionSearch}
                    setSearchValue={(val) => {
                        setJurisdiccionSearch(val);
                        // Workaround: ignorar invalidación durante el montaje
                        if (isMounting.current) return;
                        // Solo invalidar si el texto no coincide con la opción actualmente seleccionada
                        if (opcionesJurisdiccion.length > 0) {
                            const currentOption = opcionesJurisdiccion.find((o) => Number(o.id) === idJurisdiccion);
                            if (!currentOption || currentOption.descripcion !== val) {
                                setIdJurisdiccion(null);
                            }
                        }
                    }}
                    setSelected={(option) => {
                        if (option) {
                            setIdJurisdiccion(Number(option.id));
                            setJurisdiccionSearch(option.descripcion);
                        }
                    }}
                    onClear={() => {
                        // Workaround: ignorar clear durante el montaje
                        if (isMounting.current) return;
                        setIdJurisdiccion(null);
                        setJurisdiccionSearch("");
                    }}
                    hints={jurisdiccionError ? [{ variant: 'error', text: jurisdiccionError }] : []}
                    classNameContainer="w-full"
                />
            );
        }

        // Fuero
        if (fieldName === "fuero") {
            const fueroError = errors[fieldName] || "";
            const fueroDisabled = !idJurisdiccion;
            return (
                <Autocomplete
                    key={field.label}
                    label={field.label}
                    placeholder={fueroDisabled ? "Seleccionar una jurisdicción primero" : "Seleccionar fuero"}
                    opciones={fueroDisabled ? [] : opcionesFuero}
                    disabled={fueroDisabled}
                    cargando={fuerosLoading}
                    searchValue={fueroSearch}
                    setSearchValue={(val) => {
                        setFueroSearch(val);
                        // Workaround: ignorar invalidación durante el montaje
                        if (isMounting.current) return;
                        // Solo invalidar si el texto no coincide con la opción actualmente seleccionada
                        if (opcionesFuero.length > 0) {
                            const currentOption = opcionesFuero.find((o) => Number(o.id) === idFuero);
                            if (!currentOption || currentOption.descripcion !== val) {
                                setIdFuero(null);
                            }
                        }
                    }}
                    setSelected={(option) => {
                        if (option) {
                            setIdFuero(Number(option.id));
                            setFueroSearch(option.descripcion);
                        }
                    }}
                    onClear={() => {
                        // Workaround: ignorar clear durante el montaje
                        if (isMounting.current) return;
                        setIdFuero(null);
                        setFueroSearch("");
                    }}
                    hints={fueroError ? [{ variant: 'error', text: fueroError }] : []}
                    classNameContainer="w-full"
                />
            );
        }

        // Juzgado
        if (fieldName === "juzgado") {
            const juzgadoError = errors[fieldName] || "";
            const juzgadoDisabled = !idJurisdiccion || !idFuero;
            let juzgadoPlaceholder = "Seleccionar juzgado";
            if (!idJurisdiccion) {
                juzgadoPlaceholder = "Seleccionar jurisdicción primero";
            } else if (!idFuero) {
                juzgadoPlaceholder = "Seleccionar fuero primero";
            }
            return (
                <Autocomplete
                    key={field.label}
                    label={field.label}
                    placeholder={juzgadoPlaceholder}
                    opciones={juzgadoDisabled ? [] : opcionesJuzgado}
                    disabled={juzgadoDisabled}
                    cargando={juzgadosLoading}
                    searchValue={juzgadoSearch}
                    setSearchValue={(val) => {
                        setJuzgadoSearch(val);
                        // Workaround: ignorar invalidación durante el montaje
                        if (isMounting.current) return;
                        // Solo invalidar si el texto no coincide con la opción actualmente seleccionada
                        if (opcionesJuzgado.length > 0) {
                            const currentOption = opcionesJuzgado.find((o) => Number(o.id) === idJuzgado);
                            if (!currentOption || currentOption.descripcion !== val) {
                                setIdJuzgado(null);
                            }
                        }
                    }}
                    setSelected={(option) => {
                        if (option) {
                            setIdJuzgado(Number(option.id));
                            setJuzgadoSearch(option.descripcion);
                        }
                    }}
                    onClear={() => {
                        // Workaround: ignorar clear durante el montaje
                        if (isMounting.current) return;
                        setIdJuzgado(null);
                        setJuzgadoSearch("");
                    }}
                    hints={juzgadoError ? [{ variant: 'error', text: juzgadoError }] : []}
                    classNameContainer="w-full"
                />
            );
        }

        // Secretaría
        if (fieldName === "secretaria") {
            const secretariaError = errors[fieldName] || "";
            const secretariaDisabled = !idJuzgado;
            return (
                <Autocomplete
                    key={field.label}
                    label={field.label}
                    placeholder={secretariaDisabled ? "Seleccionar juzgado primero" : "Seleccionar secretaría"}
                    opciones={secretariaDisabled ? [] : opcionesSecretaria}
                    disabled={secretariaDisabled}
                    cargando={secretariasLoading}
                    searchValue={secretariaSearch}
                    setSearchValue={(val) => {
                        setSecretariaSearch(val);
                        // Workaround: ignorar invalidación durante el montaje
                        if (isMounting.current) return;
                        // Solo invalidar si el texto no coincide con la opción actualmente seleccionada
                        if (opcionesSecretaria.length > 0) {
                            const currentOption = opcionesSecretaria.find((o) => Number(o.id) === idSecretaria);
                            if (!currentOption || currentOption.descripcion !== val) {
                                setIdSecretaria(null);
                            }
                        }
                    }}
                    setSelected={(option) => {
                        if (option) {
                            setIdSecretaria(Number(option.id));
                            setSecretariaSearch(option.descripcion);
                        }
                    }}
                    onClear={() => {
                        // Workaround: ignorar clear durante el montaje
                        if (isMounting.current) return;
                        setIdSecretaria(null);
                        setSecretariaSearch("");
                    }}
                    hints={secretariaError ? [{ variant: 'error', text: secretariaError }] : []}
                    classNameContainer="w-full"
                />
            );
        }

        // Campos de texto (Input) - Expediente, Demandante, Demandado, Oficio, NroTramite
        const textFields = ["expediente", "actor", "demandado", "oficio", "nroTramite"];

        const maxCharactersMap = {
            expediente: 100,
            actor: 200,
            demandado: 200,
            oficio: 100,
            nroTramite: 50,
        };

        const placeHolderMap = {
            expediente: "Ej.: 12345/67",
            actor: "Ingresá el demandante",
            demandado: "Ingresá el nombre del demandado",
            oficio: "Ej.: 123456",
            nroTramite: "Ej.: 123456789",
        };

        if (textFields.includes(fieldName)) {
            return (
                <Input
                    key={field.label}
                    label={field.label}
                    value={fieldValue}
                    maxCharacters={maxCharactersMap[fieldName] || 0}
                    placeholder={placeHolderMap[fieldName] || ""}
                    onChange={(e) => {
                        let newValue = "";
                        if (typeof e === 'string') {
                            newValue = e;
                        } else if (e?.target?.value !== undefined) {
                            newValue = e.target.value;
                        } else if (e?.value !== undefined) {
                            newValue = e.value;
                        }
                        setValue(fieldName, newValue);
                    }}
                    hints={errorMessage ? [{ variant: 'error', text: errorMessage }] : []}
                    status={errorMessage ? 'error' : 'default'}
                />
            );
        }

        // Campo de solo lectura (Cuenta bancaria, catรกlogos)
        return (
            <div key={field.label} className="flex flex-col gap-1">
                <Text variant="body2" className="text-tertiary-60">
                    {field.label}
                </Text>
                <Text variant="body2" className="text-tertiary-10 font-semibold">
                    {field.value || "-"}
                </Text>
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="bg-tertiary-100 border border-tertiary-95 rounded py-6 px-4">
                {/* Título y botones */}
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Text variant="subtitle1" className="text-primary-10">
                            {title}
                        </Text>
                    </div>

                    <div className="flex">
                        <IconButton icon="close" size="medium" onClick={handleCancel} />
                    </div>
                </div>

                {/* Lรญnea divisoria */}
                <div className="h-px bg-tertiary-80 my-4" />

                {/* Campos */}
                <div className="flex flex-col gap-1">
                    {fields.map((field) =>
                        field.editable
                            ? renderEditableField(field)
                            : (
                                <div key={field.label} className="flex flex-col gap-1">
                                    <Text variant="body2" className="text-tertiary-60">
                                        {field.label}
                                    </Text>
                                    <Text variant="body2" className="text-tertiary-10 font-semibold">
                                        {field.value || "-"}
                                    </Text>
                                </div>
                            )
                    )}
                    <div className="flex justify-end gap-2 mt-5 max-h-9">
                        <Button variant="ghost" size="sm" onClick={handleCancel}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" size="sm" isDisabled={!isValid}>
                            Guardar
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
};

EmailBancarioEditForm.propTypes = {
    title: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string,
            editable: PropTypes.bool,
            editKey: PropTypes.string,
        })
    ),
    emailBancario: PropTypes.object,
    userName: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
};

export default EmailBancarioEditForm;
